// Shared tokens + helpers for the maintenance module.
export const MAGENTA = '#E63B7A';

export const SEVERITY_BAR = {
  low:      'bg-status-info',
  medium:   'bg-ink-yellow',
  high:     'bg-brand',
  critical: 'bg-ink-magenta',
};

export const ISSUE_STATUS_PILL = {
  open:        'bg-ink-yellow/[0.12] border-ink-yellow/[0.3] text-ink-yellow',
  in_progress: 'bg-status-info/[0.12] border-status-info/[0.3] text-status-info',
  scheduled:   'bg-status-purple/[0.12] border-status-purple/[0.3] text-status-purple',
  resolved:    'bg-status-ok/[0.12] border-status-ok/[0.3] text-status-ok',
  closed:      'border-border-strong text-text-tertiary',
  wont_fix:    'border-border-strong text-text-muted',
};

export const ISSUE_STATUS_LABEL = {
  open: 'Open', in_progress: 'In progress', scheduled: 'Scheduled',
  resolved: 'Resolved', closed: 'Closed', wont_fix: "Won't fix",
};

export const EVENT_STATUS_PILL = {
  scheduled: 'bg-status-purple/[0.12] border-status-purple/[0.3] text-status-purple',
  completed: 'bg-status-ok/[0.12] border-status-ok/[0.3] text-status-ok',
  missed:    'bg-ink-magenta/[0.18] border-ink-magenta/[0.4] text-ink-magenta',
  cancelled: 'border-border-strong text-text-tertiary',
};

export const EVENT_STATUS_LABEL = {
  scheduled: 'Scheduled', completed: 'Completed', missed: 'Missed', cancelled: 'Cancelled',
};

export const KIND_PILL = {
  routine: 'bg-status-ok/[0.12] border-status-ok/[0.3] text-status-ok',
  visit:   'bg-status-purple/[0.12] border-status-purple/[0.3] text-status-purple',
};

export const SEVERITY_LABEL = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };

export const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const formatFrequency = (s) => {
  if (!s) return '';
  if (s.frequency === 'daily') return 'Daily';
  if (s.frequency === 'weekly') return `Weekly on ${DAYS_OF_WEEK[s.dayOfWeek ?? 0]}`;
  if (s.frequency === 'monthly') return `Monthly on day ${s.dayOfMonth}`;
  if (s.frequency === 'every_n_days') return `Every ${s.intervalDays} days`;
  return s.frequency;
};

export const formatDateShort = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

export const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
};

export const formatRelative = (iso) => {
  if (!iso) return '—';
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

/** Pad a row id as a fallback ticket number, e.g. 7 → "#0007". */
const derivedTicket = (id) => (id == null ? '' : `#${String(id).padStart(4, '0')}`);

/**
 * Display ticket number. Accepts either a row object (preferred — uses its
 * stored `ticketNumber` if set) or a bare id (falls back to derived).
 */
export const ticketNumber = (rowOrId, prefix = '') => {
  if (rowOrId == null) return '';
  if (typeof rowOrId === 'object') {
    return `${prefix}${rowOrId.ticketNumber || derivedTicket(rowOrId.id)}`;
  }
  return `${prefix}${derivedTicket(rowOrId)}`;
};

/** Combine a list of printers + equipment into one selectable list. */
export const buildMachineOptions = (printers, equipment) => {
  const out = [];
  (printers ?? []).forEach((p) => out.push({
    value: `p:${p.id}`, label: p.name, kind: 'printer',
    printerId: p.id, equipmentId: null,
  }));
  (equipment ?? []).forEach((e) => out.push({
    value: `e:${e.id}`, label: e.name, kind: e.categoryName ?? 'equipment',
    printerId: null, equipmentId: e.id,
  }));
  return out;
};

export const decodeMachineValue = (value) => {
  if (!value) return { printerId: null, equipmentId: null };
  const [kind, id] = value.split(':');
  if (kind === 'p') return { printerId: Number(id), equipmentId: null };
  if (kind === 'e') return { printerId: null, equipmentId: Number(id) };
  return { printerId: null, equipmentId: null };
};

export const encodeMachineValue = ({ printerId, equipmentId }) => {
  if (printerId) return `p:${printerId}`;
  if (equipmentId) return `e:${equipmentId}`;
  return '';
};
